<template>
  <v-container class="px-0 py-0 main-wrapper">
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <CdeCard color="background" rounded="lg">
          <v-card-text class="custom-info-box">
            <p class="font-weight-bold">{{ t('please_be_advised') }}</p>
            <p>
              <span class="font-weight-bold">{{ t('fourteen_days_duration_free') }}</span>
              {{ t('is_included_however') }}
              <span class="font-weight-bold">{{ t('every_week_price') }}</span>
              {{ t('rent_fee_charge') }}
            </p>
          </v-card-text>
        </CdeCard>
      </v-col>
    </v-row>

    <v-container v-if="!isLoading" class="px-0 first-part">
      <v-row class="mx-0 mb-2 date-input-row">
        <v-col cols="6" class="pl-0 pr-2">
          <v-text-field
            :model-value="fromDateText"
            label="Stelltag"
            placeholder="TT.MM.JJJJ"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            maxlength="10"
            readonly
            class="date-input"
            :class="{ 'date-input-active': activeInput === 'from' }"
            :error-messages="fromDateError"
            @focus="activeInput = 'from'"
            @update:model-value="updateFromDateText"
            @blur="commitFromDateInput"
            @keydown.enter.prevent="commitFromDateInput" />
        </v-col>

        <v-col cols="6" class="pr-0 pl-2">
          <v-text-field
            :model-value="toDateText"
            label="Abholtag"
            placeholder="TT.MM.JJJJ"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            maxlength="10"
            readonly
            class="date-input"
            :class="{ 'date-input-active': activeInput === 'to' }"
            :error-messages="toDateError"
            @focus="activeInput = 'to'"
            @update:model-value="updateToDateText"
            @blur="commitToDateInput"
            @keydown.enter.prevent="commitToDateInput" />
        </v-col>
      </v-row>

      <v-locale-provider class="d-flex justify-center" :locale="locale">
        <v-date-picker
          ref="datePicker"
          v-model="selectedDateRange"
          color="secondary"
          hide-header
          width="100%"
          :allowed-dates="getAllowedDates"
          :min="getMinDate"
          show-adjacent-months
          multiple="range"
          :max="new Date(nextYear, 11, 31)"
          @update:model-value="setDateRange" />
      </v-locale-provider>

      <v-divider class="footer-divider my-2" />

      <v-row>
        <v-col cols="12" class="d-flex flex-column justify-center text-center pb-0 px-0">
          <span class="font-weight-bold">{{ t('placement_time_window') }}</span>
        </v-col>
        <v-col cols="12" class="px-0">
          <v-row class="pb-3 px-0">
            <v-col class="pt-0 px-0" cols="12">
              <v-radio-group
                v-model="payload.containerPlacementTime"
                class="d-flex justify-center time-radio"
                hide-details
                inline>
                <v-radio
                  color="primary"
                  class="py-2 px-4"
                  :label="CONTAINER_DELIVERY_TIME_FRAME.WHOLE_DAY"
                  :value="CONTAINER_DELIVERY_TIME_FRAME.WHOLE_DAY" />
                <v-radio
                  color="primary"
                  class="py-2 px-4"
                  :label="CONTAINER_DELIVERY_TIME_FRAME.MORNING"
                  :value="CONTAINER_DELIVERY_TIME_FRAME.MORNING" />
                <v-radio
                  color="primary"
                  class="py-2 px-4"
                  :label="CONTAINER_DELIVERY_TIME_FRAME.AFTERNOON"
                  :value="CONTAINER_DELIVERY_TIME_FRAME.AFTERNOON" />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else>
      <v-skeleton-loader type="list-item-three-line" />
      <v-skeleton-loader type="list-item-three-line" />
      <v-skeleton-loader type="list-item-three-line" />
    </v-container>

    <v-container class="px-0">
      <v-row class="pt-6">
        <v-col cols="12" class="px-0">
          <v-tooltip top :disabled="!incomplete">
            <template #activator="{ props }">
              <div v-bind="props">
                <ButtonPrimary width="100%" @click="confirmData">
                  {{ t('carry_over') }}
                </ButtonPrimary>
              </div>
            </template>
            <span>{{ t('please_complete_date_selection') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script setup lang="ts">
import { useDateUtils } from '@/composables/useDateUtils'
import { CONTAINER_DELIVERY_TIME_FRAME } from '@/types/enums'
import type { OrderDatePayload } from '@/types/types'
import { useDate } from 'vuetify'

const adapter = useDate()

const { locale, t } = useI18n()
const { getEarliestPossibleDeliveryDate, isWeekday, isPublicHoliday } = useDateUtils()

const dateStore = useDateStore()
const sidebarStore = useSidebarStore()

const properties = defineProps<{
  selectedDates: Date[]
  selectedPlacementTime: CONTAINER_DELIVERY_TIME_FRAME
}>()

const { selectedDates, selectedPlacementTime } = toRefs(properties)

const emit = defineEmits(['datesConfirmed'])

const isLoading = ref(true)
const selectedDateRange = ref<Date[]>(selectedDates.value ?? [])
const earliestPossibleDeliveryDate = ref<Date | null>(null)
const userSetEarliestPossibleDeliveryDate = ref(false)

const payload = ref<OrderDatePayload>({
  deliveryDate: [],
  containerPlacementTime: selectedPlacementTime.value,
})

const datePicker = ref()
const nextYear = new Date().getFullYear() + 1

const activeInput = ref<'from' | 'to'>('from')
const fromDateText = ref('')
const toDateText = ref('')
const fromDateError = ref('')
const toDateError = ref('')

function parsePickerDateString(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null

  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

function isDateBetween(date: Date, start: Date, end: Date) {
  const current = normalizeDate(date).getTime()
  const startTime = normalizeDate(start).getTime()
  const endTime = normalizeDate(end).getTime()
  console.log({ current: new Date(current), start: new Date(startTime), end: new Date(endTime) })
  return current > startTime && current < endTime
}

function decorateDateRange() {
  nextTick(() => {
    const root = datePicker.value?.$el
    if (!root) return

    const dayElements = Array.from(
      root.getElementsByClassName('v-date-picker-month__day'),
    ) as HTMLElement[]

    dayElements.forEach(el => {
      el.classList.remove('custom-range-start', 'custom-range-middle', 'custom-range-end')
    })

    if (selectedDateRange.value.length < 2) return

    const [startDate, endDate] = sortDatesAsc(selectedDateRange.value).map(normalizeDate)

    dayElements.forEach(el => {
      const rawDate = el.getAttribute('data-v-date')
      if (!rawDate) return

      const currentDate = parsePickerDateString(rawDate)
      if (!currentDate) return

      if (isSameDay(currentDate, startDate)) {
        el.classList.add('custom-range-start')
        return
      }

      if (isSameDay(currentDate, endDate)) {
        el.classList.add('custom-range-end')
        return
      }

      if (isDateBetween(currentDate, startDate, endDate)) {
        el.classList.add('custom-range-middle')
      }
    })
  })
}

function normalizeDate(date: Date) {
  if (!isValidDate(date)) {
    return new Date()
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function isValidDate(date: unknown): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime())
}

function isSameDay(dateA: Date | null | undefined, dateB: Date | null | undefined) {
  if (!dateA || !dateB) return false

  return normalizeDate(dateA).getTime() === normalizeDate(dateB).getTime()
}

function sortDatesAsc(dates: Date[]) {
  return [...dates].sort((a, b) => normalizeDate(a).getTime() - normalizeDate(b).getTime())
}

function formatDateForDisplay(date: Date | null | undefined) {
  if (!date || !isValidDate(date)) return ''

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}.${month}.${year}`
}

function parseDisplayDate(value: string) {
  if (!value) return null

  const trimmed = value.trim()

  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(trimmed)) return null

  const [day, month, year] = trimmed.split('.').map(Number)
  const parsedDate = new Date(year, month - 1, day)

  if (!isValidDate(parsedDate)) return null

  if (
    parsedDate.getDate() !== day ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getFullYear() !== year
  ) {
    return null
  }

  return parsedDate
}

function applyDateInputMask(value: string) {
  const digitsOnly = value.replaceAll(/\D/g, '').slice(0, 8)

  if (digitsOnly.length <= 2) return digitsOnly
  if (digitsOnly.length <= 4) return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2)}`
  return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 4)}.${digitsOnly.slice(4)}`
}

function isSelectableDate(date: Date) {
  return getAllowedDates(date)
}

watch(
  selectedDateRange,
  newDateRange => {
    const normalizedDates = newDateRange.filter(isValidDate).map(normalizeDate)

    payload.value.deliveryDate = normalizedDates

    fromDateText.value = formatDateForDisplay(normalizedDates[0])
    toDateText.value = formatDateForDisplay(normalizedDates[1])

    decorateDateRange()

    if (
      normalizedDates.length > 0 &&
      earliestPossibleDeliveryDate.value &&
      (!isSameDay(normalizedDates[0], earliestPossibleDeliveryDate.value) ||
        !normalizedDates.some(date => isSameDay(date, earliestPossibleDeliveryDate.value)))
    ) {
      userSetEarliestPossibleDeliveryDate.value = false
    }
  },
  { deep: true, immediate: true },
)

function setDateRange(dateRange: Date[]) {
  if (!Array.isArray(dateRange)) {
    selectedDateRange.value = []
    manageTooltip()
    return
  }

  const validDates = dateRange.filter(isValidDate)

  if (!validDates.length) {
    selectedDateRange.value = []
    manageTooltip()
    return
  }

  if (validDates.length === 1) {
    selectedDateRange.value = [normalizeDate(validDates[0])]
    activeInput.value = 'to'
    manageTooltip()
    return
  }

  const sortedDates = sortDatesAsc(validDates).map(normalizeDate)
  selectedDateRange.value = [sortedDates[0], sortedDates[sortedDates.length - 1]]
  activeInput.value = 'to'
  manageTooltip()
}

function updateFromDateText(value: string) {
  fromDateText.value = applyDateInputMask(value)
  fromDateError.value = ''
}

function updateToDateText(value: string) {
  toDateText.value = applyDateInputMask(value)
  toDateError.value = ''
}

function commitFromDateInput() {
  fromDateError.value = ''

  if (!fromDateText.value) {
    const currentToDate = selectedDateRange.value[1]
    selectedDateRange.value = currentToDate ? [normalizeDate(currentToDate)] : []
    activeInput.value = 'from'
    manageTooltip()
    return
  }

  const parsedFromDate = parseDisplayDate(fromDateText.value)

  if (!parsedFromDate) {
    fromDateError.value = 'Bitte Datum im Format TT.MM.JJJJ eingeben'
    return
  }

  if (!isSelectableDate(parsedFromDate)) {
    fromDateError.value = 'Dieses Datum ist nicht auswählbar'
    return
  }

  const normalizedFromDate = normalizeDate(parsedFromDate)
  const currentToDate = selectedDateRange.value[1]

  if (!currentToDate) {
    selectedDateRange.value = [normalizedFromDate]
    activeInput.value = 'to'
    manageTooltip()
    return
  }

  if (normalizedFromDate.getTime() > normalizeDate(currentToDate).getTime()) {
    selectedDateRange.value = [normalizedFromDate, normalizedFromDate]
    activeInput.value = 'to'
    manageTooltip()
    return
  }

  selectedDateRange.value = [normalizedFromDate, normalizeDate(currentToDate)]
  activeInput.value = 'to'
  manageTooltip()
}

function commitToDateInput() {
  toDateError.value = ''

  if (!toDateText.value) {
    const currentFromDate = selectedDateRange.value[0]
    selectedDateRange.value = currentFromDate ? [normalizeDate(currentFromDate)] : []
    activeInput.value = 'to'
    manageTooltip()
    return
  }

  const parsedToDate = parseDisplayDate(toDateText.value)

  if (!parsedToDate) {
    toDateError.value = 'Bitte Datum im Format TT.MM.JJJJ eingeben'
    return
  }

  if (!isSelectableDate(parsedToDate)) {
    toDateError.value = 'Dieses Datum ist nicht auswählbar'
    return
  }

  const normalizedToDate = normalizeDate(parsedToDate)
  const currentFromDate = selectedDateRange.value[0]

  if (!currentFromDate) {
    selectedDateRange.value = [normalizedToDate]
    activeInput.value = 'from'
    manageTooltip()
    return
  }

  if (normalizedToDate.getTime() < normalizeDate(currentFromDate).getTime()) {
    selectedDateRange.value = [normalizeDate(currentFromDate), normalizeDate(currentFromDate)]
    activeInput.value = 'to'
    manageTooltip()
    return
  }

  selectedDateRange.value = [normalizeDate(currentFromDate), normalizedToDate]
  activeInput.value = 'to'
  manageTooltip()
}

function manageTooltip() {
  nextTick(() => {
    const currentTooltip = document.querySelector('.custom-tooltip')

    if (selectedDateRange.value.length === 1) {
      if (currentTooltip) currentTooltip.remove()

      const startDate = selectedDateRange.value[0]

      const element = Array.from(
        datePicker.value?.$el.getElementsByClassName('v-date-picker-month__day') ?? [],
      ).find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (el: any) => el.attributes.getNamedItem('data-v-date')?.value === adapter.toISO(startDate),
      ) as HTMLElement | undefined

      if (!element) return

      const tooltip = element.appendChild(document.createElement('div'))
      tooltip.classList?.add('custom-tooltip')
      tooltip.classList?.add('text-center')

      const tooltipText =
        activeInput.value === 'to'
          ? t('now_select_pickup_day')
          : 'Bitte zuerst den Stelltag auswählen'

      tooltip.appendChild(document.createElement('span')).textContent = tooltipText
      return
    }

    if (currentTooltip) currentTooltip.remove()
  })
}

function getAllowedDates(val: unknown) {
  if (!(val instanceof Date)) return false

  return isWeekday(val) && !isPublicHoliday(val)
}

const getMinDate = computed(() => {
  return earliestPossibleDeliveryDate.value &&
    earliestPossibleDeliveryDate.value.getTime() > Date.now()
    ? earliestPossibleDeliveryDate.value
    : new Date()
})

watch(
  selectedDateRange,
  newDateRange => {
    const normalizedDates = newDateRange.filter(isValidDate).map(normalizeDate)

    payload.value.deliveryDate = normalizedDates

    fromDateText.value = formatDateForDisplay(normalizedDates[0])
    toDateText.value = formatDateForDisplay(normalizedDates[1])

    if (
      normalizedDates.length > 0 &&
      earliestPossibleDeliveryDate.value &&
      (!isSameDay(normalizedDates[0], earliestPossibleDeliveryDate.value) ||
        !normalizedDates.some(date => isSameDay(date, earliestPossibleDeliveryDate.value)))
    ) {
      userSetEarliestPossibleDeliveryDate.value = false
    }
  },
  { deep: true, immediate: true },
)

watch(
  selectedPlacementTime,
  newPlacementTime => {
    payload.value.containerPlacementTime = newPlacementTime
  },
  { immediate: true },
)

const confirmData = () => {
  if (
    !payload.value.deliveryDate.length ||
    payload.value.deliveryDate.length < 2 ||
    !payload.value.containerPlacementTime ||
    payload.value.containerPlacementTime === null
  )
    return

  emit('datesConfirmed', payload.value)
  sidebarStore.closeSidebar()
}

const incomplete = computed(() => {
  return (
    !payload.value.deliveryDate.length ||
    payload.value.deliveryDate.length < 2 ||
    !payload.value.containerPlacementTime ||
    payload.value.containerPlacementTime === null
  )
})

onNuxtReady(async () => {
  await dateStore.getPublicHolidaysFromApi()

  earliestPossibleDeliveryDate.value = await getEarliestPossibleDeliveryDate()

  isLoading.value = false

  nextTick(() => {
    manageTooltip()
    decorateDateRange()
  })
})
</script>

<style lang="scss" scoped>
div:deep(.v-date-picker-month) {
  padding: 0;
}

:deep(.v-label--clickable) {
  max-width: 80px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #000;
}

:deep(.v-selection-control--inline) {
  display: flex;
  flex-direction: column !important;
}

:deep(.v-date-picker-controls__month .v-btn--density-default.v-btn--icon.v-btn--variant-text) {
  border: black 1px solid;
}

.custom-styling {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin-top: 10px;
  margin-bottom: 10px;
}

.custom-info-box {
  font-size: 12px;
}

.footer-divider {
  border: 1px solid rgb(var(--v-theme-border));
}

.show-date {
  border: rgb(var(--v-theme-text)) 1px solid;
  color: rgb(var(--v-theme-text));

  &.active {
    color: rgb(var(--v-theme-primary));
    border: rgb(var(--v-theme-primary)) 1px solid;
  }
}

.date-input-row {
  align-items: flex-start;
}

.date-input :deep(.v-field) {
  border-radius: 12px;
}

.date-input-active :deep(.v-field) {
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}

.date-input :deep(.v-field__input) {
  font-weight: 600;
}

.date-input :deep(.v-label.v-field-label) {
  font-size: 12px;
  font-weight: 700;
}

div:deep(.custom-tooltip) {
  position: absolute;
  z-index: 10001 !important;
  width: 160px;
  font-size: 12px;
  background-color: white;
  border-radius: 10px;
  padding: 4px;
  border: 1px solid rgb(var(--v-theme-border));
  top: -182%;
  left: -145%;
  overflow: visible;
}

div:deep(.v-date-picker-month__day--week-start .custom-tooltip) {
  left: -20%;
}

div:deep(.v-date-picker-month__day--week-start .custom-tooltip:before) {
  left: 15%;
}

div:deep(.v-date-picker-month__day-btn.v-btn--disabled) {
  background-color: rgb(var(--v-theme-surface-darken2)) !important;
  color: rgb(var(--v-theme-text)) !important;
}

div:deep(.custom-tooltip::before) {
  content: '';
  position: absolute;
  transform: rotate(45deg);
  top: 93%;
  left: 45%;
  background-color: white;
  border-bottom: 1px solid rgb(var(--v-theme-border));
  border-right: 1px solid rgb(var(--v-theme-border));
  padding: 5px;
  z-index: 1;
}

.v-tooltip,
.v-tooltip * {
  z-index: 10001 !important;
}

.main-wrapper {
  height: 85vh;
}

.first-part {
  max-height: 70%;
  overflow-y: auto;
}

.time-radio :deep(label) {
  font-weight: 300 !important;
  color: black !important;
  opacity: 1 !important;
  max-width: 76px;
}

@media (max-height: 815px) {
  .first-part {
    max-height: 65%;
  }
}

@media screen and (max-width: 599px) {
  .main-wrapper {
    height: 80vh;
  }

  .first-part {
    max-height: 40vh;
  }

  div:deep(.v-radio-group .v-input__control) {
    width: 100% !important;
  }

  div:deep(.v-selection-control-group) {
    justify-content: space-around !important;
  }

  div:deep(.v-radio) {
    padding: 8px 2px !important;
  }
}

:deep(.v-date-picker-month__days) {
  display: flex !important;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 340px;
  column-gap: 0;
}
:deep(.v-date-picker-month__day) {
  margin-top: 12px;
  margin-right: 8px;
  transform: scale(1.2);
}
:deep(.v-date-picker-month__day--selected .v-btn) {
  border-radius: 10% 50% 50% 50%;
  transform: rotate(135deg);
  .v-btn__content {
    transform: rotate(-135deg);
  }
}
:deep(
    .v-date-picker-month__day--selected
      + .v-date-picker-month__day:not(.v-date-picker-month__day--selected)
      .v-btn
  ) {
  background: none !important;
}

:deep(.v-date-picker-month__day--selected ~ .v-date-picker-month__day--selected .v-btn) {
  transform: rotate(-45deg);
  .v-btn__content {
    transform: rotate(45deg);
  }
}

:deep(.v-date-picker-month__day--selected:first-of-type .v-btn) {
  display: none;
}
div:deep(.v-date-picker-month__day.custom-range-middle) {
  background: rgba(98, 63, 232, 0.1);
  .v-btn {
    background: none !important;
  }
}

div:deep(.v-date-picker-month__day.custom-range-start) {
  background: linear-gradient(to right, transparent 50%, rgba(98, 63, 232, 0.1) 50%);
}

div:deep(.v-date-picker-month__day.custom-range-end) {
  background: rgba(98, 63, 232, 0.1); //fallback for browsers that don't support gradients
  background: linear-gradient(to right, rgba(98, 63, 232, 0.1) 50%, transparent 50%);
  border-top-right-radius: 999px;
  border-bottom-right-radius: 999px;
}

div:deep(.v-date-picker-month__day.custom-range-start) {
  border-top-left-radius: 999px;
  border-bottom-left-radius: 999px;
}
</style>
